import React, { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { adminApi, getAdminUser, setAdminToken } from "@/api/adminOneClient";
import AdminChyron from "@/components/adminone/AdminChyron";
import OrgSelector from "@/components/adminone/OrgSelector";
import OracleLogo from "@/components/OracleLogo";

const NAV = [
  { to: "/adminone/dashboard", label: "Dashboard", icon: "▦" },
  { to: "/adminone/inbox", label: "Inbox · Grants & Funding", icon: "✉" },
  { to: "/adminone/core-satellite", label: "Core-Satellite™", icon: "◎" },
  { to: "/adminone/system-readiness", label: "System Readiness", icon: "❖" },
  { to: "/adminone/ops", label: "Operations", icon: "✦" },
  { to: "/adminone/signals", label: "Signal Command", icon: "◇" },
  // Phase 33 — Predictive AI + Enterprise Intelligence Infrastructure (additive)
  { to: "/adminone/funding-intelligence",   label: "Funding Intelligence™",   icon: "💰" },
  { to: "/adminone/lifecommand",            label: "LifeCommand™ Aggregate",  icon: "🫀" },
  { to: "/adminone/membership-control",     label: "CEO Membership Control™", icon: "🎟" },
  { to: "/adminone/live-data",              label: "Live Data Center™",       icon: "🛰" },
  { to: "/adminone/live-llm",               label: "Live LLM Engine™",        icon: "◉" },
  { to: "/adminone/content-generator",      label: "Content Generator™",      icon: "🎬" },
  { to: "/adminone/market-abuse-awareness", label: "Market Abuse Awareness™", icon: "⚖" },
  { to: "/adminone/system-architecture",    label: "System Architecture™",    icon: "🛰" },
  { to: "/oracle/capital",                  label: "Global Capital Command™", icon: "🏛", external: true },
  { to: "/intelligence-globe",              label: "Intelligence Globe™",     icon: "🌐", external: true },
  { to: "/adminone/telemetry",              label: "Enterprise Telemetry™",   icon: "📡" },
  { to: "/adminone/logix2", label: "LOGIX-2™", icon: "◉" },
  { to: "/adminone/opportunity-radar", label: "Opportunity Radar™", icon: "✺" },
  { to: "/adminone/diagnostics", label: "Diagnostics", icon: "♥" },
  { to: "/adminone/chyron", label: "Chyron™", icon: "▒" },
  { to: "/adminone/africa", label: "Africa™", icon: "🌍" },
  { to: "/adminone/global-ai", label: "Global AI™", icon: "🤖" },
  { to: "/adminone/intelligence", label: "Oracle Intelligence™", icon: "⚡" },
  { to: "/adminone/oracle-watchtowers", label: "ORACLE Watchtowers™", icon: "🛰" },
  { to: "/adminone/signal-command", label: "Signal Command™", icon: "◈" },
  { to: "/adminone/scanner-command", label: "Scanner Command™", icon: "🛰️" },
  { to: "/adminone/notification-fabric", label: "Notification Fabric™", icon: "📡" },
  { to: "/adminone/startups-grants", label: "Startups & Grants", icon: "🚀" },
  { to: "/adminone/briefing-center", label: "Briefing Center", icon: "📰" },
  { to: "/adminone/uganda-phase2", label: "Uganda™ Phase 2", icon: "🇺🇬" },
  { to: "/adminone/watchtowers", label: "Watchtowers", icon: "◎" },
  { to: "/adminone/ingestion", label: "Signal Ingestion", icon: "⌖" },
  { to: "/adminone/services", label: "Service Intelligence", icon: "❖" },
  { to: "/adminone/healthcare-map", label: "Healthcare Map", icon: "✚" },
  { to: "/adminone/oracle-feed", label: "ORACLE Feed", icon: "☰" },
  { to: "/adminone/oanda", label: "OANDA Center", icon: "⋈" },
  { to: "/adminone/reports", label: "Reports", icon: "☰" },
  { to: "/adminone/compliance", label: "Compliance", icon: "⚑" },
  { to: "/adminone/audit", label: "Audit Log", icon: "▸" },
  { to: "/adminone/users", label: "Users", icon: "○" },
  { to: "/adminone/sessions", label: "Sessions", icon: "▣" },
  { to: "/adminone/membership", label: "Membership", icon: "★" },
  { to: "/adminone/membership-analytics", label: "Membership Analytics", icon: "▲" },
  { to: "/adminone/rate-limits", label: "Rate Limits", icon: "⌛" },
  { to: "/adminone/orgs", label: "Organizations", icon: "▤" },
  { to: "/adminone/settings", label: "Settings", icon: "⚙" },
];

export default function AdminOneLayout() {
  const [chyron, setChyron] = useState(null); // null lets AdminChyron poll live
  const [status, setStatus] = useState({});
  const [channels, setChannels] = useState({});
  const [navOpen, setNavOpen] = useState(false);
  const user = getAdminUser();
  const navigate = useNavigate();

  useEffect(() => {
    let mounted = true;
    adminApi
      .dashboard()
      .then(({ data }) => {
        if (!mounted) return;
        if (Array.isArray(data?.chyron) && data.chyron.length) setChyron(data.chyron);
        setStatus(data?.status || {});
      })
      .catch(() => {});
    adminApi.channelsStatus().then(({ data }) => {
      if (mounted) setChannels(data?.channels || {});
    }).catch(() => {});
    return () => { mounted = false; };
  }, []);

  const onSignOut = async () => {
    try { await adminApi.logout(); } catch {}
    setAdminToken(null);
    navigate("/adminone/login", { replace: true });
  };

  const webhooksOn = channels && Object.values(channels).some(Boolean);

  return (
    <div className="admin-shell mesh-app" data-testid="admin-shell">
      {/* Bold global ambient animation layer (GPU-efficient, reduced-motion safe) */}
      <div className="mesh-ambient" aria-hidden="true">
        <div className="mesh-ambient__field" />
        <div className="mesh-ambient__stream mesh-ambient__stream--1" />
        <div className="mesh-ambient__stream mesh-ambient__stream--2" />
        <div className="mesh-ambient__stream mesh-ambient__stream--3" />
        <div className="mesh-ambient__pulse" />
      </div>

      <header className="admin-header">
        <div className="admin-header__brand">
          <button
            className="admin-navbtn"
            type="button"
            onClick={() => setNavOpen((v) => !v)}
            aria-label="Toggle navigation"
          >
            ☰
          </button>
          <div className="admin-header__crest" aria-hidden="true">
            <OracleLogo size={36} withRing title="CareLogix ORACLE — GIOS" />
          </div>
          <div className="admin-header__titles">
            <div className="admin-header__h1">CareLogix™ ORACLE <span className="admin-header__sep">·</span> AdminOne <span className="mesh-badge-v2">GIOS™</span></div>
            <div className="admin-header__h2">Global Intelligence Operating System™ · Profit & Service Intelligence Command Center™</div>
          </div>
        </div>
        <div className="admin-header__badges">
          <span className="admin-badge is-on">ADMINONE ONLINE</span>
          <span className={`admin-badge ${status.mongo === "connected" ? "is-on" : "is-off"}`}>
            MONGO {status.mongo === "connected" ? "CONNECTED" : "DISCONNECTED"}
          </span>
          <span className={`admin-badge ${status.anthropic === "configured" ? "is-on" : "is-off"}`}>
            LLM {status.anthropic === "configured" ? "READY" : "MISSING"}
          </span>
          <span className={`admin-badge ${webhooksOn ? "is-on" : "is-warn"}`}>
            WEBHOOKS {webhooksOn ? "ACTIVE" : "STANDBY"}
          </span>
          <span className="admin-badge is-on">PDF READY</span>
          <span className="admin-badge is-on">COMPLIANCE</span>
          <span className="admin-badge is-on">SIGNAL FABRIC</span>
          <span className="admin-badge is-on">WATCHTOWERS</span>
          <span className={`admin-badge ${status.service_intelligence === "online" ? "is-on" : "is-warn"}`}>SVC INTEL</span>
          <span className={`admin-badge ${status.healthcare_map === "online" ? "is-on" : "is-warn"}`}>HEALTHCARE</span>
          <span className={`admin-badge ${status.market_data === "online" ? "is-on" : "is-warn"}`}>
            MKT DATA {status.market_data === "online" ? "LIVE" : "STANDBY"}
          </span>
          <span className={`admin-badge ${status.notifications === "online" ? "is-on" : "is-warn"}`}>
            NOTIFY {status.notifications === "online" ? "ON" : "STANDBY"}
          </span>
          <span className={`admin-badge ${status.oanda === "configured" ? "is-on" : "is-warn"}`}>
            OANDA {status.oanda === "configured" ? "LIVE" : "STANDBY"}
          </span>
        </div>
        <div className="admin-header__user">
          <OrgSelector />
          <Link to="/" className="admin-linkbtn" title="Back to ORACLE">← ORACLE</Link>
          {user && (
            <span className="admin-userpill" data-testid="admin-userpill">
              <span className="admin-userpill__name">{user.name || user.email}</span>
              <span className="admin-userpill__role">{user.role}</span>
              <span className="admin-userpill__tier" data-testid="admin-userpill-tier" title="Membership tier">ENTERPRISE</span>
            </span>
          )}
          <button type="button" className="admin-linkbtn" onClick={onSignOut} data-testid="admin-signout">
            Sign Out
          </button>
        </div>
      </header>

      <div className="admin-main">
        <nav className={`admin-rail ${navOpen ? "is-open" : ""}`} aria-label="AdminOne navigation">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              className={({ isActive }) => `admin-rail__link ${isActive ? "is-active" : ""}`}
              onClick={() => setNavOpen(false)}
              data-testid={`admin-rail-${n.to.split("/").pop()}`}
            >
              <span className="admin-rail__icon" aria-hidden="true">{n.icon}</span>
              <span>{n.label}</span>
            </NavLink>
          ))}
          <div className="mesh-rail-foot">
            <div className="mesh-rail-foot__title">LOGIX-2™ · GIOS™</div>
            <div className="mesh-rail-foot__row"><span className="mesh-rail-foot__dot is-on" /> LOGIX-2 Engine</div>
            <div className="mesh-rail-foot__row"><span className="mesh-rail-foot__dot is-on" /> Signal Fabric</div>
            <div className="mesh-rail-foot__row"><span className="mesh-rail-foot__dot is-on" /> 8 Watchtowers</div>
            <div className="mesh-rail-foot__row"><span className="mesh-rail-foot__dot is-on" /> 26 CareLogix Services</div>
            <div className="mesh-rail-foot__row"><span className="mesh-rail-foot__dot is-on" /> Healthcare Map</div>
            <div className="mesh-rail-foot__row"><span className={`mesh-rail-foot__dot ${webhooksOn ? "is-on" : "is-warn"}`} /> Notification Fabric</div>
          </div>
        </nav>
        <main className="admin-canvas">
          <Outlet />
          <footer className="admin-footer">
            AdminOne is an internal operational dashboard. CareLogix ORACLE is research and education
            only. It does not provide financial advice, investment advice, medical advice, betting guarantees,
            trade execution, or fiduciary services. High-impact decisions require human review.
          </footer>
        </main>
      </div>

      <AdminChyron items={chyron} speed="normal" severity={status.oanda === "configured" ? "normal" : "warning"} />
    </div>
  );
}
